import React from "react"

import Flex from "@irati/ui/src/Flex"
import idHelper from "@irati/utils/id"
import {determineDealThumbnailImage} from "@irati/deals/helpers/deals/appearance"
import withBranding from "@irati/brand/withBranding"
import Spacing from "@irati/ui/src/Spacing"
import {H1, H5} from "@irati/ui/src/Typography"
import {getTimeLimitedType} from "@irati/deals/helpers/deals/isTimeLimited"
import {isDealUnavailable} from "@irati/deals/helpers/deals/isDealUnavailable"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import isRPDeal from "@irati/deals/helpers/deals/isRPDeal"
import perksTracking from "@irati-tracking/perks/perks"
import mainTheme from "@irati/ui/src/global/theme"

import DealHighlight, {DealHighlightRow} from "../../DealHighlight"
import {shouldDisplayDealAvatar} from "../../../utils/deals/imageUtils"
import DealPages from "../../../enums/DealPages"
import DEAL_PAGES from "../../../constants/dealPage"
import SharePerk from "../../SharePerk"
import FavouriteIconButton, {FavouriteButtonOnChange} from "../../Favourite/IconButton"
import DealAvatar from "./styled/DealAvatar"
import UnavailableDeal from "./UnavailableDeal"
import {HeaderProps} from "."

const DealHeader: React.FC<HeaderProps> = ({deal, branding, ...props}) => {
  const thumbnailImageSrc = shouldDisplayDealAvatar(deal) ? determineDealThumbnailImage(deal, branding) : null
  const id = idHelper("deal-header-details", props)

  const {deliveryHighlight} = deal
  const typeHighlight = getTimeLimitedType(deal)
  const isRewards = isRPDeal(deal)
  const showBrandLink = deal.brandName && deal?.brand?.slug && !isRewards
  const handleTracking = () => {
    perksTracking.clickBrand({
      label: deal.brandName!,
      id: deal.brandId!,
      source: DealPages.PERK
    })
  }

  const handleFavChange: FavouriteButtonOnChange = (isFavourite: boolean) => {
    const func = isFavourite ? perksTracking.favouritePerk : perksTracking.unfavouritePerk
    func(deal, DEAL_PAGES.PERK)
  }

  return (
    <>
      <Flex justifyContent="space-between">
        <Flex>
          <div>
            {thumbnailImageSrc && (
              <DealAvatar data-test="top-thumbnail" src={thumbnailImageSrc} className="dealInfoBlock_dealThumbnail" withShadow />
            )}
          </div>
          <Spacing paddingLeft={4}>
            <Flex direction="column">
              {showBrandLink && (
                <div>
                  <ButtonRouterLink tertiary cta to={`/brands/${deal?.brand?.slug}`} onClick={handleTracking}>
                    <Flex alignItems="center" gap={1}>
                      {deal.brandName} <Icon icon={IconTypes.chevronRight} />
                    </Flex>
                  </ButtonRouterLink>
                </div>
              )}
              {deal.brandName && isRewards && <H5 color={mainTheme.colors.greys.grey50}>{deal.brandName}</H5>}
              <H1 marginBottom={2}>{deal.header}</H1>
              <DealHighlight>
                <DealHighlightRow id={idHelper("type", id)} type={typeHighlight} size={20} />
                <DealHighlightRow id={idHelper("delivery", id)} type={deliveryHighlight} size={20} />
              </DealHighlight>
            </Flex>
          </Spacing>
        </Flex>
        <Flex>
          <SharePerk deal={deal} />
          {!isRPDeal(deal) && <FavouriteIconButton deal={deal} onChange={handleFavChange} />}
        </Flex>
      </Flex>
      {isDealUnavailable(deal) && (
        <Spacing marginTop={2}>
          <UnavailableDeal />
        </Spacing>
      )}
    </>
  )
}

export default withBranding(DealHeader)
